import { useConfigStore } from '@/client/services/state/configStore';
import { useEffect } from 'react';

export const useCustomFavicon = () => {
  const { config } = useConfigStore();

  const faviconUrl = `${config.publicImageUrl}${config.layout}/favicons/favicon.ico`;

  const defaultFavicons = {
    appleTouchIcon: '/public/favicons/apple-touch-icon.png',
    favicon32: '/public/favicons/favicon-32x32.png',
    favicon16: '/public/favicons/favicon-16x16.png',
    maskIcon: '/public/favicons/safari-pinned-tab.svg',
  };

  const setFavicon = (url: string, type: 'ico' | '32' | '16' | 'apple' | 'mask') => {
    const selector =
      type === 'ico'
        ? 'link[rel*="icon"]:not([rel*="apple"]):not([rel*="mask"])'
        : type === 'apple'
        ? 'link[rel*="apple-touch-icon"]'
        : type === 'mask'
        ? 'link[rel*="mask-icon"]'
        : `link[rel*="icon"][sizes="${type === '32' ? '32x32' : '16x16'}"]`;

    const existingLink = document.querySelector(selector) as HTMLLinkElement;
    const link = existingLink || document.createElement('link');

    if (type === 'ico') {
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = url;
    } else if (type === 'apple') {
      link.rel = 'apple-touch-icon';
      link.setAttribute('sizes', '180x180');
      link.href = url;
    } else if (type === 'mask') {
      link.rel = 'mask-icon';
      link.href = url;
      link.setAttribute('color', '#00bcf1');
    } else {
      link.rel = 'icon';
      link.type = 'image/png';
      link.setAttribute('sizes', type === '32' ? '32x32' : '16x16');
      link.href = url;
    }

    document.getElementsByTagName('head')[0].appendChild(link);
  };

  const setDefaultFavicons = () => {
    setFavicon(defaultFavicons.appleTouchIcon, 'apple');
    setFavicon(defaultFavicons.favicon32, '32');
    setFavicon(defaultFavicons.favicon16, '16');
    setFavicon(defaultFavicons.maskIcon, 'mask');
  };

  const isSafari = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    return ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Chromium');
  };

  const checkFaviconExists = async () => {
    try {
      try {
        const response = await fetch(faviconUrl, {
          method: 'HEAD',
          mode: 'no-cors',
        });
        if (response.ok) return true;
      } catch {
        // continue
      }
      const imageExists = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = faviconUrl;
      });
      return imageExists;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Safari: use default favicons only (custom favicon does not work in Safari)
    if (isSafari()) {
      setDefaultFavicons();
      return;
    }

    const isConfigLoaded =
      config.publicImageUrl &&
      config.layout &&
      config.publicImageUrl !== '' &&
      config.layout !== '' &&
      Object.keys(config.theme).length > 0;

    if (isConfigLoaded) {
      checkFaviconExists().then((exists) => {
        if (exists) {
          setFavicon(faviconUrl, 'ico');
          setFavicon(faviconUrl, 'apple');
        } else {
          setDefaultFavicons();
        }
      });
    }
  }, [config.publicImageUrl, config.layout]);
};
